import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported, Messaging } from 'firebase/messaging';
import axios from 'axios';
import { API_BASE, FIREBASE_CONFIG, FIREBASE_VAPID_KEY } from '../config';
import { getToken as getAuthToken } from '../auth';
import { storeNotification } from './notificationStore';

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let initPromise: Promise<Messaging | null> | null = null;

async function ensureMessaging(): Promise<Messaging | null> {
  if (messaging) return messaging;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    if (!FIREBASE_CONFIG.projectId || !FIREBASE_VAPID_KEY) return null;
    if (!(await isSupported().catch(() => false))) return null;
    if (!app) app = getApps()[0] ?? initializeApp(FIREBASE_CONFIG);
    messaging = getMessaging(app);
    return messaging;
  })();

  return initPromise;
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

export async function registerFcmToken(): Promise<void> {
  try {
    const m = await ensureMessaging();
    if (!m) return;

    const granted = await requestNotificationPermission();
    if (!granted) return;

    const swRegistration = await navigator.serviceWorker
      .register('/firebase-messaging-sw.js', { scope: '/firebase-cloud-messaging-push-scope' })
      .catch(() => null);

    const fcmToken = await getToken(m, {
      vapidKey: FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swRegistration ?? undefined,
    });
    if (!fcmToken) return;

    const authToken = await getAuthToken();
    if (!authToken) return;

    await axios.post(
      `${API_BASE}/auth/fcm-token`,
      { fcmToken },
      { headers: { Authorization: `Bearer ${authToken}` } },
    );
  } catch {
    // best-effort
  }
}

export function setupForegroundNotificationHandler(): () => void {
  let unsubscribe: (() => void) | null = null;
  ensureMessaging().then(m => {
    if (!m) return;
    unsubscribe = onMessage(m, payload => {
      const data: any = payload.data || {};
      const title = (payload.notification?.title) || data.title || 'AGA';
      const body = (payload.notification?.body) || data.body || '';
      const type = data.type || 'BROADCAST';
      const route = data.route;
      storeNotification({ title, body, type, apkUrl: data.apkUrl, route });
    });
  });
  return () => { if (unsubscribe) unsubscribe(); };
}

// Resolve the in-app web path a notification should deep-link to.
// Prefers an explicit `route` (if it already looks like a path or a known
// mobile route name), otherwise maps known notification types. Returns null
// when there is no sensible destination.
export function routeForNotification(data: { type?: string; route?: string }): string | null {
  const mapRoute = (r: string): string | null => {
    if (r.startsWith('/')) return r;
    switch (r) {
      case 'Leaderboard': return '/leaderboard';
      case 'Events': return '/events';
      case 'Profile': return '/profile?openHistory=1';
      default: return null;
    }
  };

  if (data.route) {
    const mapped = mapRoute(data.route.trim());
    if (mapped) return mapped;
  }

  switch (data.type) {
    case 'reminder_leaderboard':
    case 'LEADERBOARD_PRIZE':
      return '/leaderboard';
    case 'reminder_streak':
    case 'happy_hour_start':
    case 'happy_hour_ending':
    case 'STREAK':
    case 'BONUS':
      return '/events';
    // Payment / order activity → the player's history.
    case 'order':
    case 'topup':
    case 'withdraw':
    case 'redemption':
    case 'reminder_wallet':
    case 'TOPUP_COMPLETED':
    case 'TOPUP_COMPLETED_WINBACK':
    case 'REFERRAL_PAID':
      return '/profile?openHistory=1';
    default:
      return null;
  }
}
